import { Text } from '@airwallex/typography';
import React, { useMemo } from 'react';
import { formatMoney } from '@/utils/formatPayment';
import { formatCurrency } from '@airwallex/awx-currency-formatter';
import { TotalSectionStack, TotalRow, Divider } from './style';
import { useTranslation } from 'react-i18next';
import { useTabletOrMobile } from '@/hooks/useMediaQuery';
import { CreditNoteDetail } from '../types';
import {
  creditNoteHasInvoiceDiscount,
  creditNoteHasLineItemDiscount,
  creditNoteHasTax,
  creditWithLineItem,
} from '@/utils/creditNoteLayout';

type Props = {
  creditNote: CreditNoteDetail;
};

const CreditNoteTotalSectionV2: React.FC<Props> = ({ creditNote }) => {
  const { t } = useTranslation();
  const {
    subtotalAmountExcludeTax,
    totalDiscountAmountExcludeTax,
    totalAmountExcludeTax,
    totalAmount,
    totalTaxAmount,
    currency,
  } = creditNote;
  const isTabletOrMobile = useTabletOrMobile();

  const hasInvoiceDiscount = useMemo(() => creditNoteHasInvoiceDiscount(creditNote), [creditNote]);
  const hasLineItemDiscount = useMemo(() => creditNoteHasLineItemDiscount(creditNote), [creditNote]);
  const hasDiscount = useMemo(
    () => hasInvoiceDiscount || hasLineItemDiscount,
    [hasInvoiceDiscount, hasLineItemDiscount]
  );
  const creditWithItem = useMemo(
    () => (creditNote.lineItems ?? []).some((item) => creditWithLineItem(item)),
    [creditNote]
  );

  return (
    <TotalSectionStack data-test="credit-note-total-section" space="12px" isTabletOrMobile={isTabletOrMobile}>
      {hasDiscount ? (
        <>
          <TotalRow>
            <Text variant="body">
              {creditWithItem ? t('creditNote.subtotalQtyTimesUnit') : t('creditNote.subtotalExclTax')}
            </Text>
            <Text variant="body">{formatMoney(subtotalAmountExcludeTax ?? 0, currency)}</Text>
          </TotalRow>
          <TotalRow>
            <Text variant="body">{t('invoice.discounts')}</Text>
            <Text variant="body">-{formatMoney(totalDiscountAmountExcludeTax ?? 0, currency)}</Text>
          </TotalRow>
          <TotalRow>
            <Divider />
          </TotalRow>
        </>
      ) : null}
      <TotalRow>
        <Text variant="body">{t('creditNote.amountExclTax')}</Text>
        <Text variant="body">{formatMoney(totalAmountExcludeTax ?? 0, currency)}</Text>
      </TotalRow>
      <TotalRow>
        <Text variant="body">{t('invoice.salesTax')}</Text>
        <Text variant="body">{formatMoney(totalTaxAmount, currency)}</Text>
      </TotalRow>
      <TotalRow>
        <Divider />
      </TotalRow>
      <TotalRow>
        <Text variant="bodyBold">{t('creditNote.creditTotalInclTax')}</Text>
        <Text variant="bodyBold">
          {formatCurrency({ amount: totalAmount, currency, showSymbol: true, showCode: true })}
        </Text>
      </TotalRow>
    </TotalSectionStack>
  );
};

export default CreditNoteTotalSectionV2;
